/* ─────────────────────────────────────────────────────────────────────
   Tykables 7.0 — Native FX (window.tykNative)

   Centralised helpers for "feels native on mobile" features:
     • prefs           — localStorage-backed user preferences
     • haptic(kind)    — Vibration API (opt-out + prefers-reduced-motion)
     • share(opts)     — Web Share API with copy-link fallback
     • transition(fn)  — View Transitions API wrapper (same-document)
     • fullscreen(el)  — Fullscreen API with graceful fallback
     • keepAwake()     — Wake Lock API (acquire / release / auto-renew)
     • autofocus(el)   — Focus first focusable, remember previous
     • toast(msg, k)   — Minimal toast (used for share fallback, errors)

   All methods are safe no-ops on unsupported browsers. Nothing throws
   past the window boundary.
   ───────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  // ── Preferences ─────────────────────────────────────────────────────
  const PREF_KEY = 'tyk.prefs.v1';
  const DEFAULTS = { haptics: true };

  function readPrefs() {
    try {
      const raw = localStorage.getItem(PREF_KEY);
      if (!raw) return Object.assign({}, DEFAULTS);
      const parsed = JSON.parse(raw);
      return Object.assign({}, DEFAULTS, parsed);
    } catch (e) {
      return Object.assign({}, DEFAULTS);
    }
  }

  function writePrefs(patch) {
    try {
      const next = Object.assign(readPrefs(), patch || {});
      localStorage.setItem(PREF_KEY, JSON.stringify(next));
      document.dispatchEvent(new CustomEvent('tyk:prefs:change', { detail: next }));
      return next;
    } catch (e) {
      return readPrefs();
    }
  }

  const prefs = {
    get: function (key) {
      const p = readPrefs();
      return key ? p[key] : p;
    },
    set: function (key, value) { return writePrefs({ [key]: value }); },
    toggle: function (key) { return writePrefs({ [key]: !readPrefs()[key] }); },
  };

  // ── Haptics ─────────────────────────────────────────────────────────
  // Patterns are deliberately subtle. iOS ignores navigator.vibrate() and
  // that's fine — it silently no-ops.
  const PATTERNS = {
    tap:      10,                  // primary CTA tap
    success:  [18, 40, 18],        // add-to-cart / confirm
    warning:  [30, 60, 30],        // gentle warning
    error:    [55, 40, 55, 40, 55],// validation / network error
    select:   8,                   // variant pill pick, checkbox
  };

  const reducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

  function haptic(kind) {
    if (!('vibrate' in navigator)) return;
    if (!prefs.get('haptics')) return;
    if (reducedMotion && reducedMotion.matches) return;
    const pattern = PATTERNS[kind] || PATTERNS.tap;
    try { navigator.vibrate(pattern); } catch (e) { /* no-op */ }
  }

  // ── Web Share ───────────────────────────────────────────────────────
  function cleanTitle(raw) {
    if (!raw) return document.title;
    // Strip common " – Shop Name" or " - Shop Name" suffix for cleaner shares.
    return String(raw).replace(/\s+[–-]\s+[^–-]+$/, '').trim() || raw;
  }

  async function share(opts) {
    const data = {
      url: (opts && opts.url) || location.href,
      title: cleanTitle((opts && opts.title) || document.title),
    };

    if (navigator.share) {
      try {
        await navigator.share(data);
        haptic('success');
        return { shared: true };
      } catch (e) {
        // AbortError = user dismissed sheet; don't fall back.
        if (e && e.name === 'AbortError') return { shared: false, aborted: true };
        // Otherwise fall through to clipboard.
      }
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(data.url);
        toast('Link copied', 'success');
        haptic('success');
        return { shared: true, copied: true };
      } catch (e) { /* fall through */ }
    }

    // Absolute last resort: a text prompt
    try { window.prompt('Copy this link', data.url); } catch (e) {}
    return { shared: false };
  }

  // ── View Transitions ────────────────────────────────────────────────
  function transition(fn) {
    if (typeof fn !== 'function') return Promise.resolve();
    if (reducedMotion && reducedMotion.matches) {
      try { fn(); } catch (e) {}
      return Promise.resolve();
    }
    if (!document.startViewTransition) {
      try { fn(); } catch (e) {}
      return Promise.resolve();
    }
    try {
      const vt = document.startViewTransition(fn);
      return vt.finished.catch(() => {});
    } catch (e) {
      try { fn(); } catch (err) {}
      return Promise.resolve();
    }
  }

  // ── Fullscreen ──────────────────────────────────────────────────────
  function fullscreen(el) {
    if (!el) return Promise.resolve(false);
    const req = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen;
    if (!req) return Promise.resolve(false);
    try {
      const p = req.call(el);
      return (p && p.then) ? p.then(() => true).catch(() => false) : Promise.resolve(true);
    } catch (e) {
      return Promise.resolve(false);
    }
  }

  function exitFullscreen() {
    const exit = document.exitFullscreen || document.webkitExitFullscreen || document.msExitFullscreen;
    if (!exit) return Promise.resolve();
    try {
      const p = exit.call(document);
      return (p && p.then) ? p.catch(() => {}) : Promise.resolve();
    } catch (e) { return Promise.resolve(); }
  }

  // ── Wake Lock ───────────────────────────────────────────────────────
  let wakeSentinel = null;
  let wakeRefCount = 0;

  async function keepAwakeAcquire() {
    if (!('wakeLock' in navigator)) return false;
    wakeRefCount++;
    if (wakeSentinel) return true;
    try {
      wakeSentinel = await navigator.wakeLock.request('screen');
      wakeSentinel.addEventListener('release', () => { wakeSentinel = null; });
      return true;
    } catch (e) {
      wakeSentinel = null;
      return false;
    }
  }

  async function keepAwakeRelease() {
    if (wakeRefCount > 0) wakeRefCount--;
    if (wakeRefCount > 0) return;
    if (wakeSentinel) {
      try { await wakeSentinel.release(); } catch (e) {}
      wakeSentinel = null;
    }
  }

  // Re-acquire on visibility change if consumers still want it.
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible' && wakeRefCount > 0 && !wakeSentinel) {
      // Silent re-acquire; any failure is logged by the API itself.
      navigator.wakeLock && navigator.wakeLock.request('screen')
        .then(s => { wakeSentinel = s; s.addEventListener('release', () => { wakeSentinel = null; }); })
        .catch(() => {});
    }
  });

  // Auto-wire elements with [data-tyk-keep-awake] — e.g. size finder quiz.
  // Acquires on mount/visible, releases on detach/hidden.
  function autoWireKeepAwake(root) {
    const scope = root || document;
    scope.querySelectorAll('[data-tyk-keep-awake]').forEach(function (el) {
      if (el.__tykKeepAwake) return;
      el.__tykKeepAwake = true;
      keepAwakeAcquire();
      // Release when removed from DOM
      const mo = new MutationObserver(() => {
        if (!document.contains(el)) {
          keepAwakeRelease();
          mo.disconnect();
        }
      });
      mo.observe(document.body, { childList: true, subtree: true });
    });
  }

  // ── Autofocus ───────────────────────────────────────────────────────
  const FOCUSABLE =
    'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const focusStack = [];

  function autofocus(container, opts) {
    if (!container) return;
    const prev = document.activeElement;
    // Prefer a close button on drawers/modals (safer, no surprise keyboard).
    const closeBtn =
      container.querySelector('[data-drawer-close], .drawer__close, .modal__close, [aria-label*="close" i]');
    const firstFocusable = container.querySelector(FOCUSABLE);
    const target = (opts && opts.target) || closeBtn || firstFocusable;
    if (target) {
      // Prevent iOS scroll-jump by using preventScroll where supported.
      try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); }
      focusStack.push({ container: container, prev: prev });
    }
  }

  function restoreFocus(container) {
    for (let i = focusStack.length - 1; i >= 0; i--) {
      if (focusStack[i].container === container) {
        const prev = focusStack[i].prev;
        focusStack.splice(i, 1);
        if (prev && typeof prev.focus === 'function') {
          try { prev.focus({ preventScroll: true }); } catch (e) { prev.focus(); }
        }
        return;
      }
    }
  }

  // ── Toast ───────────────────────────────────────────────────────────
  let toastHost = null;
  function toast(message, kind) {
    if (!toastHost) {
      toastHost = document.createElement('div');
      toastHost.className = 'tyk-toast-host';
      toastHost.setAttribute('role', 'status');
      toastHost.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastHost);
    }
    const node = document.createElement('div');
    node.className = 'tyk-toast' + (kind ? ' tyk-toast--' + kind : '');
    node.textContent = String(message || '');
    toastHost.appendChild(node);
    requestAnimationFrame(() => node.classList.add('is-visible'));
    setTimeout(() => {
      node.classList.remove('is-visible');
      setTimeout(() => node.remove(), 300);
    }, 2400);
  }

  // ── Integrations ────────────────────────────────────────────────────
  function wirePubSubHaptics() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    // Track cartUpdate source to avoid buzzing on passive section refreshes.
    let lastCartCount = null;
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      const data = event && event.cartData;
      if (!data || typeof data.item_count !== 'number') return;
      // Only buzz if this update actually added/removed an item.
      if (lastCartCount !== null && data.item_count !== lastCartCount) {
        haptic('success');
      }
      lastCartCount = data.item_count;
    });

    subscribe(PUB_SUB_EVENTS.cartError, function () { haptic('error'); });

    // Gentle tick on variant/option selection
    subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, function () { haptic('select'); });

    // Quantity stepper bumps
    subscribe(PUB_SUB_EVENTS.quantityUpdate, function () { haptic('tap'); });
  }

  function wireCartDrawerFocus() {
    const drawers = document.querySelectorAll('cart-drawer, .cart-drawer');
    drawers.forEach(function (drawer) {
      // Watch for `.active` class being added (theme's open signal).
      const mo = new MutationObserver(function (muts) {
        for (let i = 0; i < muts.length; i++) {
          if (muts[i].attributeName !== 'class') continue;
          if (drawer.classList.contains('active')) {
            // Small delay so the drawer's own transitionend focus doesn't
            // fight ours.
            setTimeout(function () { autofocus(drawer); }, 40);
          } else {
            restoreFocus(drawer);
          }
        }
      });
      mo.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    });
  }

  function wireQuickViewFocus() {
    const qv = document.getElementById('tyk-quick-view');
    if (!qv) return;
    const mo = new MutationObserver(function () {
      const visible = qv.getAttribute('aria-hidden') === 'false' || qv.classList.contains('is-open');
      if (visible) {
        setTimeout(function () { autofocus(qv); }, 40);
      } else {
        restoreFocus(qv);
      }
    });
    mo.observe(qv, { attributes: true, attributeFilter: ['aria-hidden', 'class'] });
  }

  // Wrap variant DOM swaps in a view transition for smooth cross-fades.
  // product-info.js fetches and replaces content; we can't easily patch it
  // without breaking future Dawn updates, so we observe the product-info
  // element and apply a transition class during swaps.
  function wireVariantTransitions() {
    if (!('startViewTransition' in document)) return;
    const hosts = document.querySelectorAll('product-info, .product-info, [id^="MainProduct-"]');
    hosts.forEach(function (host) {
      host.style.setProperty('view-transition-name', 'product-info-' + (host.id || 'main'));
    });
  }

  // Upgrade PDP share buttons to use tykNative.share (cleaner title).
  function wireShareButtons() {
    document.addEventListener('click', function (e) {
      const t = e.target && e.target.closest && e.target.closest('[data-tyk-share]');
      if (!t) return;
      e.preventDefault();
      share({
        url: t.getAttribute('data-share-url') || location.href,
        title: t.getAttribute('data-share-title') || document.title,
      });
    });
  }

  // Fullscreen helper — any [data-tyk-fullscreen] toggles FS on its target.
  function wireFullscreenToggles() {
    document.addEventListener('click', function (e) {
      const t = e.target && e.target.closest && e.target.closest('[data-tyk-fullscreen]');
      if (!t) return;
      const selector = t.getAttribute('data-tyk-fullscreen');
      const target = selector ? document.querySelector(selector) : t;
      if (!target) return;
      if (document.fullscreenElement || document.webkitFullscreenElement) {
        exitFullscreen();
      } else {
        fullscreen(target).then(function (ok) { if (ok) haptic('tap'); });
      }
    });
  }

  // Preferences UI — hooks [data-tyk-pref] checkboxes and
  // [data-tyk-pref-radio] radio groups to the prefs store.
  function applyTheme(value) {
    const root = document.documentElement;
    if (value === 'light' || value === 'dark') {
      root.setAttribute('data-theme', value);
    } else {
      // 'auto' — let prefers-color-scheme decide
      root.removeAttribute('data-theme');
    }
  }

  function wirePreferencesUI() {
    // Boolean checkboxes
    const checks = document.querySelectorAll('input[type="checkbox"][data-tyk-pref]');
    checks.forEach(function (el) {
      const key = el.getAttribute('data-tyk-pref');
      el.checked = !!prefs.get(key);
      el.addEventListener('change', function () {
        prefs.set(key, el.checked);
        if (key === 'haptics' && el.checked) haptic('success');
      });
    });

    // Radio groups (e.g. theme: auto/light/dark)
    const radios = document.querySelectorAll('input[type="radio"][data-tyk-pref-radio]');
    radios.forEach(function (el) {
      const key = el.getAttribute('data-tyk-pref-radio');
      const current = prefs.get(key) || 'auto';
      if (el.value === current) el.checked = true;
      el.addEventListener('change', function () {
        if (!el.checked) return;
        prefs.set(key, el.value);
        if (key === 'theme') applyTheme(el.value);
        haptic('select');
      });
    });

    // React to external pref changes (another tab, etc.)
    document.addEventListener('tyk:prefs:change', function (e) {
      checks.forEach(function (el) {
        const key = el.getAttribute('data-tyk-pref');
        el.checked = !!e.detail[key];
      });
      radios.forEach(function (el) {
        const key = el.getAttribute('data-tyk-pref-radio');
        el.checked = (e.detail[key] || 'auto') === el.value;
      });
      if (e.detail.theme) applyTheme(e.detail.theme);
    });
  }

  // ── Visual Viewport — keyboard-aware layout ─────────────────────────
  // Exposes --tyk-vvh (visual viewport height) and --tyk-vvw on :root
  // and a [data-tyk-keyboard='open'] attribute on <html> when the soft
  // keyboard occupies > 25% of the viewport. Lets CSS reposition bottom-
  // anchored UI (cart drawer footer, quiz footer, sticky CTAs) when the
  // mobile keyboard appears so inputs aren't obscured.
  function wireVisualViewport() {
    if (!window.visualViewport) return;
    const vv = window.visualViewport;
    const root = document.documentElement;
    let ticking = false;

    function update() {
      ticking = false;
      const h = vv.height;
      const w = vv.width;
      const wh = window.innerHeight || h;
      root.style.setProperty('--tyk-vvh', h + 'px');
      root.style.setProperty('--tyk-vvw', w + 'px');
      const keyboardLikely = (wh - h) > (wh * 0.25);
      if (keyboardLikely) {
        root.setAttribute('data-tyk-keyboard', 'open');
      } else {
        root.removeAttribute('data-tyk-keyboard');
      }
    }
    function schedule() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }
    vv.addEventListener('resize', schedule);
    vv.addEventListener('scroll', schedule);
    update();
  }

  // ── Screen Orientation — lock during fullscreen image/lookbook ──────
  // Exposes window.tykNative.orientation.lock(type) / .unlock().
  // type: 'landscape' | 'portrait' | 'any'. Failures are silent — older
  // browsers (Safari) don't support the API, that's fine.
  const orientation = {
    lock: function (type) {
      if (!screen.orientation || !screen.orientation.lock) return Promise.resolve(false);
      return screen.orientation.lock(type || 'landscape')
        .then(() => true)
        .catch(() => false);
    },
    unlock: function () {
      if (!screen.orientation || !screen.orientation.unlock) return;
      try { screen.orientation.unlock(); } catch (e) {}
    },
    type: function () {
      return (screen.orientation && screen.orientation.type) || null;
    },
  };

  // Auto-lock landscape when the product media modal goes fullscreen.
  document.addEventListener('fullscreenchange', function () {
    const fsEl = document.fullscreenElement;
    if (fsEl && fsEl.matches && fsEl.matches('.product-media-modal__content, [data-tyk-lock-landscape]')) {
      orientation.lock('landscape');
    } else {
      orientation.unlock();
    }
  });

  // ── Init ────────────────────────────────────────────────────────────
  function init() {
    autoWireKeepAwake(document);
    wirePubSubHaptics();
    wireCartDrawerFocus();
    wireQuickViewFocus();
    wireVariantTransitions();
    wireShareButtons();
    wireFullscreenToggles();
    wirePreferencesUI();
    wireVisualViewport();

    // Observe future insertions (quiz section injected later, prefs UI, etc.)
    if ('MutationObserver' in window) {
      const mo = new MutationObserver((muts) => {
        let touched = false;
        for (let i = 0; i < muts.length; i++) {
          const added = muts[i].addedNodes;
          for (let j = 0; j < added.length; j++) {
            if (added[j].nodeType === 1) {
              autoWireKeepAwake(added[j]);
              touched = true;
            }
          }
        }
        if (touched) wirePreferencesUI();
      });
      if (document.body) mo.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ── Public API ──────────────────────────────────────────────────────
  window.tykNative = {
    prefs: prefs,
    haptic: haptic,
    share: share,
    transition: transition,
    fullscreen: fullscreen,
    exitFullscreen: exitFullscreen,
    keepAwake: { acquire: keepAwakeAcquire, release: keepAwakeRelease },
    orientation: orientation,
    autofocus: autofocus,
    restoreFocus: restoreFocus,
    toast: toast,
  };
})();
